import { Particles, ParticlesProvider, type ParticlesPluginRegistrar } from '@tsparticles/react';
import { loadSlim } from '@tsparticles/slim';
import type { ISourceOptions } from '@tsparticles/engine';

interface ParticlesBackgroundProps {
  /**
   * Canvas id. Needs to be unique per mounted instance — tsparticles keys its
   * containers by id, so two instances sharing one both fight over the same
   * canvas. Overridden by the asset detail overlay, which renders its own
   * instance above the shell's.
   */
  id?: string;
  /**
   * Positioning. The shell's instance covers the viewport; one rendered inside
   * a panel wants `absolute`, so it is clipped to that panel instead of
   * escaping to the window.
   */
  position?: 'fixed' | 'absolute';
}

// Module-level so every instance hands ParticlesProvider the same callback —
// it loads the engine once per app and throws if the callback changes.
const initEngine: ParticlesPluginRegistrar = async (engine) => {
  await loadSlim(engine);
};

// Module-level so re-renders don't hand Particles a new object, which would
// destroy and reload the container.
const PARTICLES_OPTIONS: ISourceOptions = {
  fullScreen: { enable: false },
  background: {
    color: { value: 'transparent' },
  },
  fpsLimit: 60,
  particles: {
    color: { value: '#94a3b8' },
    links: {
      color: '#94a3b8',
      distance: 150,
      enable: true,
      opacity: 0.2,
      width: 1,
    },
    move: {
      direction: 'none',
      enable: true,
      outModes: { default: 'bounce' },
      random: false,
      speed: 1.3,
      straight: false,
    },
    number: {
      density: { enable: true },
      value: 140,
    },
    opacity: { value: 0.3 },
    shape: { type: 'circle' },
    size: { value: { min: 1, max: 3 } },
  },
  detectRetina: true,
};

export function ParticlesBackground({
  id = 'tsparticles',
  position = 'fixed',
}: ParticlesBackgroundProps = {}) {
  return (
    <ParticlesProvider init={initEngine}>
      <div className={`${position} inset-0 pointer-events-none`} style={{ zIndex: 0 }}>
        <Particles id={id} options={PARTICLES_OPTIONS} />
      </div>
    </ParticlesProvider>
  );
}
